import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
    fetchTasks,
    fetchSummary,
    fetchTaskDetail,
    runOnce,
    pushActivePermissions,
    requeueFailed,
    requeueTask,
    cancelTask,
} from "../api/accessSyncApi";
import { useAuth } from "../contexts/AuthContext";
import {
    Play,
    RotateCcw,
    RefreshCw,
    AlertCircle,
    CheckCircle2,
    Clock,
    Cpu,
    Search,
    X,
    Send,
    ShieldCheck,
    ShieldAlert,
    CreditCard,
    Fingerprint,
    UserCheck,
    UserX,
    KeyRound,
    Eye,
    XCircle,
    Loader2,
    Server,
    Filter,
    ChevronLeft,
    ChevronRight,
    Activity,
} from "lucide-react";

const STATUS_META = {
    pending: {
        label: "Kuyrukta Bekliyor",
        badgeClass:
            "bg-amber-50 text-amber-700 border-amber-200/80 dark:bg-amber-900/25 dark:text-amber-300 dark:border-amber-800/50",
        dotClass: "bg-amber-500",
        Icon: Clock,
    },
    running: {
        label: "Cihaza Gönderiliyor",
        badgeClass:
            "bg-blue-50 text-blue-700 border-blue-200/80 dark:bg-blue-900/25 dark:text-blue-300 dark:border-blue-800/50",
        dotClass: "bg-blue-500 animate-pulse",
        Icon: Activity,
    },
    succeeded: {
        label: "Cihaza İletildi",
        badgeClass:
            "bg-emerald-50 text-emerald-700 border-emerald-200/80 dark:bg-emerald-900/25 dark:text-emerald-300 dark:border-emerald-800/50",
        dotClass: "bg-emerald-500",
        Icon: CheckCircle2,
    },
    failed: {
        label: "İletilemedi (Hata)",
        badgeClass:
            "bg-rose-50 text-rose-700 border-rose-200/80 dark:bg-rose-900/25 dark:text-rose-300 dark:border-rose-800/50",
        dotClass: "bg-rose-500",
        Icon: XCircle,
    },
};

function resolveStatusKey(row) {
    const state = row?.state;
    if (state === 0) return "pending";
    if (state === 1) return "running";
    if (state === 2) return "succeeded";
    if (state === 3) return "failed";

    const raw = String(row?.status || "").toLowerCase();
    if (raw === "pending" || raw === "queued") return "pending";
    if (raw === "running" || raw === "processing" || raw === "sent") return "running";
    if (raw === "succeeded" || raw === "success") return "succeeded";
    if (raw === "failed" || raw === "error") return "failed";
    return "pending";
}

function formatDateTime(value) {
    if (!value) return "—";
    const dt = new Date(value);
    if (Number.isNaN(dt.getTime())) return "—";
    return dt.toLocaleString("tr-TR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
    });
}

function getOperationBadge(row) {
    const cmd = row?.commandType || row?.operationCode || "";
    const cred = String(row?.credentialType || "").toUpperCase();

    if (cmd === "CardUpsert") {
        if (cred === "USER") {
            return {
                Icon: UserCheck,
                color: "bg-indigo-50 text-indigo-700 border-indigo-200/70 dark:bg-indigo-900/25 dark:text-indigo-300 dark:border-indigo-800/50",
            };
        }
        return {
            Icon: CreditCard,
            color: "bg-emerald-50 text-emerald-700 border-emerald-200/70 dark:bg-emerald-900/25 dark:text-emerald-300 dark:border-emerald-800/50",
        };
    }
    if (cmd === "CardDelete") {
        return {
            Icon: UserX,
            color: "bg-rose-50 text-rose-700 border-rose-200/70 dark:bg-rose-900/25 dark:text-rose-300 dark:border-rose-800/50",
        };
    }
    if (cmd === "SetUserRight") {
        return {
            Icon: KeyRound,
            color: "bg-blue-50 text-blue-700 border-blue-200/70 dark:bg-blue-900/25 dark:text-blue-300 dark:border-blue-800/50",
        };
    }
    if (cmd === "FingerPrintUpsert" || cmd === "FingerPrintDelete") {
        return {
            Icon: Fingerprint,
            color: "bg-purple-50 text-purple-700 border-purple-200/70 dark:bg-purple-900/25 dark:text-purple-300 dark:border-purple-800/50",
        };
    }
    return {
        Icon: Cpu,
        color: "bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-700/60 dark:text-gray-300 dark:border-gray-600",
    };
}

export default function AccessSyncPage() {
    const { permissions } = useAuth();

    const canView = useMemo(() => permissions?.includes("access_sync:view"), [permissions]);
    const canRun = useMemo(() => permissions?.includes("access_sync:run"), [permissions]);

    // Filters
    const [statusFilter, setStatusFilter] = useState("all");
    const [categoryFilter, setCategoryFilter] = useState("access"); // "access" | "all"
    const [commandTypeFilter, setCommandTypeFilter] = useState("all");
    const [selectedDeviceId, setSelectedDeviceId] = useState("");
    const [searchText, setSearchText] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");

    // Pagination
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);
    const [totalCount, setTotalCount] = useState(0);

    // Data
    const [items, setItems] = useState([]);
    const [summary, setSummary] = useState({
        total: 0,
        pending: 0,
        running: 0,
        failed: 0,
        succeeded: 0,
        succeededLast24h: 0,
        unprocessed: 0,
        oldestPendingCreatedAt: null,
        lastRunUtc: null,
        deviceStats: [],
    });

    // UI States
    const [loading, setLoading] = useState(true);
    const [runningAction, setRunningAction] = useState(false);
    const [autoRefresh, setAutoRefresh] = useState(true);
    const [message, setMessage] = useState(null);
    const [error, setError] = useState(null);

    // Detail Modal
    const [detailModalOpen, setDetailModalOpen] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailData, setDetailData] = useState(null);

    // Search debounce
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchText.trim());
            setPage(1);
        }, 300);
        return () => clearTimeout(timer);
    }, [searchText]);

    const loadSummary = useCallback(async () => {
        if (!canView) return;
        try {
            const s = await fetchSummary({
                category: categoryFilter,
                deviceId: selectedDeviceId ? Number(selectedDeviceId) : undefined,
            });
            setSummary({
                total: s?.total ?? 0,
                pending: s?.pending ?? s?.pendingCount ?? 0,
                running: s?.running ?? 0,
                failed: s?.failed ?? s?.failedCount ?? 0,
                succeeded: s?.succeeded ?? s?.succeededTotal ?? s?.success ?? 0,
                succeededLast24h: s?.succeededLast24h ?? 0,
                unprocessed: s?.unprocessed ?? 0,
                oldestPendingCreatedAt: s?.oldestPendingCreatedAt ?? null,
                lastRunUtc: s?.lastRunUtc ?? null,
                deviceStats: Array.isArray(s?.deviceStats) ? s.deviceStats : [],
            });
        } catch (e) {
            console.warn("Summary yüklenemedi:", e);
        }
    }, [canView, categoryFilter, selectedDeviceId]);

    const loadTasks = useCallback(
        async (silent = false) => {
            if (!canView) {
                setError("Bu sayfayı görme yetkiniz yok.");
                setLoading(false);
                return;
            }
            if (!silent) setLoading(true);
            setError(null);
            try {
                const res = await fetchTasks({
                    status: statusFilter === "all" ? "" : statusFilter,
                    category: categoryFilter,
                    commandType: commandTypeFilter === "all" ? "" : commandTypeFilter,
                    deviceId: selectedDeviceId ? Number(selectedDeviceId) : undefined,
                    search: debouncedSearch || undefined,
                    page,
                    pageSize,
                });
                const list = res?.items ?? res?.data?.items ?? res?.data ?? [];
                setItems(Array.isArray(list) ? list : []);
                setTotalCount(res?.total ?? (Array.isArray(list) ? list.length : 0));
            } catch (e) {
                if (!silent) setError(e?.message || "Kuyruk kayıtları yüklenemedi.");
            } finally {
                if (!silent) setLoading(false);
            }
        },
        [canView, statusFilter, categoryFilter, commandTypeFilter, selectedDeviceId, debouncedSearch, page, pageSize]
    );

    useEffect(() => {
        loadTasks();
        loadSummary();
    }, [loadTasks, loadSummary]);

    // Auto refresh every 10 seconds
    useEffect(() => {
        if (!autoRefresh || !canView) return;
        const interval = setInterval(() => {
            loadTasks(true);
            loadSummary();
        }, 10000);
        return () => clearInterval(interval);
    }, [autoRefresh, canView, loadTasks, loadSummary]);

    const refreshAll = async () => {
        await Promise.all([loadTasks(), loadSummary()]);
    };

    const handleRunOnce = async (targetDeviceId = null) => {
        if (!canRun) return;
        setRunningAction(true);
        setMessage(null);
        setError(null);
        try {
            const devId = targetDeviceId ?? (selectedDeviceId ? Number(selectedDeviceId) : null);
            const res = await runOnce(100, devId);
            setMessage(
                `Senkronizasyon tetiklendi. Bekleyen ${res?.pendingInQueue ?? 0} işlem için cihaz servislerine bildirim gönderildi.`
            );
            await refreshAll();
        } catch (e) {
            setError(e?.message || "Senkron başlatılamadı.");
        } finally {
            setRunningAction(false);
        }
    };

    const handlePushActive = async (targetDeviceId = null) => {
        if (!canRun) return;
        setRunningAction(true);
        setMessage(null);
        setError(null);
        try {
            const devId = targetDeviceId ?? (selectedDeviceId ? Number(selectedDeviceId) : null);
            const res = await pushActivePermissions({ deviceId: devId });
            if ((res?.enqueued ?? 0) === 0) {
                setMessage(res?.message || "Gönderilecek aktif yetki eşleşmesi bulunamadı.");
            } else {
                setMessage(
                    `${res.employees} personel ve ${res.devices} cihaz için toplam ${res.enqueued} adet yetki kaydı gönderim kuyruğuna eklendi.`
                );
            }
            await refreshAll();
        } catch (e) {
            setError(e?.message || "Aktif yetkiler kuyruğa gönderilemedi.");
        } finally {
            setRunningAction(false);
        }
    };

    const handleRequeueFailed = async (targetDeviceId = null) => {
        if (!canRun) return;
        setRunningAction(true);
        setMessage(null);
        setError(null);
        try {
            const devId = targetDeviceId ?? (selectedDeviceId ? Number(selectedDeviceId) : null);
            const res = await requeueFailed({
                olderThanMinutes: 0,
                deviceId: devId,
                category: categoryFilter,
            });
            setMessage(`${res?.requeued ?? 0} adet hatalı işlem yeniden gönderilmek üzere kuyruğa alındı.`);
            await refreshAll();
        } catch (e) {
            setError(e?.message || "Hatalı işlemler tekrar kuyruğa eklenemedi.");
        } finally {
            setRunningAction(false);
        }
    };

    const handleRetryRow = async (taskId) => {
        if (!canRun || !taskId) return;
        setRunningAction(true);
        setMessage(null);
        setError(null);
        try {
            await requeueTask(taskId);
            setMessage("Seçilen işlem yeniden gönderilmek üzere kuyruğa alındı.");
            await refreshAll();
        } catch (e) {
            setError(e?.message || "İşlem tekrar kuyruğa eklenemedi.");
        } finally {
            setRunningAction(false);
        }
    };

    const handleCancelRow = async (taskId) => {
        if (!canRun || !taskId) return;
        setRunningAction(true);
        setMessage(null);
        setError(null);
        try {
            await cancelTask(taskId);
            setMessage("Kuyruktaki işlem iptal edildi.");
            await refreshAll();
        } catch (e) {
            setError(e?.message || "İşlem iptal edilemedi.");
        } finally {
            setRunningAction(false);
        }
    };

    const openDetailModal = async (row) => {
        setDetailModalOpen(true);
        setDetailData(row);
        if (!row?.id) return;
        setDetailLoading(true);
        try {
            const full = await fetchTaskDetail(row.id);
            if (full) setDetailData((prev) => ({ ...prev, ...full }));
        } catch (e) {
            console.warn("Detay alınamadı:", e);
        } finally {
            setDetailLoading(false);
        }
    };

    const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

    if (!canView) {
        return (
            <div className="p-6">
                <div className="rounded-2xl bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800/50 p-5 flex items-center gap-3 text-rose-700 dark:text-rose-300">
                    <AlertCircle className="w-5 h-5 shrink-0" />
                    <span className="text-sm font-medium">
                        Yetki senkronizasyon sayfasını görüntüleme yetkiniz bulunmuyor.
                    </span>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6 pb-8">
            {/* Page Header & Action Bar */}
            <div className="rounded-2xl bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700/80 shadow-sm p-5 sm:p-6">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                    <div className="flex items-start gap-3.5">
                        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-500 to-blue-600 text-white flex items-center justify-center shadow-md shadow-indigo-500/20 shrink-0">
                            <Server className="w-6 h-6" />
                        </div>
                        <div>
                            <h1 className="text-xl font-bold text-gray-900 dark:text-white tracking-tight">
                                Cihaz Yetki Senkronizasyonu
                            </h1>
                            <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-1">
                                Cihazlara gönderilen kart, NFC, QR, parmak izi ve geçiş yetkilerinin hangi cihazlara iletildiğini, bekleyenleri ve hata alan işlemleri takip edin.
                            </p>
                        </div>
                    </div>

                    {/* Top Action Buttons */}
                    <div className="flex flex-wrap items-center gap-2.5">
                        <button
                            type="button"
                            onClick={() => setAutoRefresh((p) => !p)}
                            className={`inline-flex items-center gap-2 px-3.5 py-2 text-xs font-medium rounded-xl border transition-all ${
                                autoRefresh
                                    ? "bg-emerald-50/80 dark:bg-emerald-900/25 border-emerald-200 dark:border-emerald-800/60 text-emerald-700 dark:text-emerald-300"
                                    : "bg-gray-50 dark:bg-gray-700/50 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300"
                            }`}
                            title="10 saniyede bir otomatik yenile"
                        >
                            <span
                                className={`w-2 h-2 rounded-full ${
                                    autoRefresh ? "bg-emerald-500 animate-ping" : "bg-gray-400"
                                }`}
                            />
                            Canlı Takip {autoRefresh ? "Açık" : "Kapalı"}
                        </button>

                        <button
                            type="button"
                            onClick={refreshAll}
                            disabled={loading || runningAction}
                            className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs sm:text-sm font-medium rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
                        >
                            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                            Yenile
                        </button>

                        {canRun && (
                            <>
                                <button
                                    type="button"
                                    onClick={() => handleRequeueFailed()}
                                    disabled={runningAction || (summary.failed === 0 && summary.running === 0)}
                                    className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs sm:text-sm font-medium rounded-xl border border-amber-200 dark:border-amber-800/60 bg-amber-50 dark:bg-amber-900/25 text-amber-800 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-colors disabled:opacity-45"
                                    title="Hata alan tüm yetkileri tekrar kuyruğa al"
                                >
                                    <RotateCcw className="w-4 h-4" />
                                    Hatalıları Tekrarla ({summary.failed})
                                </button>

                                <button
                                    type="button"
                                    onClick={() => handleRunOnce()}
                                    disabled={runningAction}
                                    className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs sm:text-sm font-medium rounded-xl border border-emerald-200 dark:border-emerald-800/60 bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm transition-colors disabled:opacity-50"
                                    title="Bekleyen yetki kuyruğunu tetikle"
                                >
                                    <Play className="w-4 h-4" />
                                    Senkronu Çalıştır
                                </button>

                                <button
                                    type="button"
                                    onClick={() => handlePushActive()}
                                    disabled={runningAction}
                                    className="inline-flex items-center gap-1.5 px-4 py-2 text-xs sm:text-sm font-medium rounded-xl bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 text-white shadow-sm shadow-indigo-500/20 transition-all disabled:opacity-50"
                                    title="Aktif yetki gruplarındaki tüm personellerin kart ve parmak izi yetkilerini cihazlara yeniden gönder"
                                >
                                    <Send className="w-4 h-4" />
                                    Aktif Yetkileri Cihazlara Eşitle
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Alerts */}
            {message && (
                <div className="rounded-2xl bg-emerald-50 dark:bg-emerald-900/25 border border-emerald-200/80 dark:border-emerald-800/60 px-4 py-3.5 flex items-center justify-between gap-3 text-emerald-800 dark:text-emerald-200 shadow-sm">
                    <div className="flex items-center gap-2.5 text-sm font-medium">
                        <CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400 shrink-0" />
                        <span>{message}</span>
                    </div>
                    <button
                        type="button"
                        onClick={() => setMessage(null)}
                        className="p-1 rounded-lg hover:bg-emerald-100 dark:hover:bg-emerald-800/50"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>
            )}

            {error && (
                <div className="rounded-2xl bg-rose-50 dark:bg-rose-900/25 border border-rose-200/80 dark:border-rose-800/60 px-4 py-3.5 flex items-center justify-between gap-3 text-rose-800 dark:text-rose-200 shadow-sm">
                    <div className="flex items-center gap-2.5 text-sm font-medium">
                        <AlertCircle className="w-5 h-5 text-rose-600 dark:text-rose-400 shrink-0" />
                        <span>{error}</span>
                    </div>
                    <button
                        type="button"
                        onClick={() => setError(null)}
                        className="p-1 rounded-lg hover:bg-rose-100 dark:hover:bg-rose-800/50"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>
            )}

            {/* 5 Interactive KPI Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                {/* Toplam */}
                <button
                    type="button"
                    onClick={() => {
                        setStatusFilter("all");
                        setPage(1);
                    }}
                    className={`text-left rounded-2xl p-4 border transition-all ${
                        statusFilter === "all"
                            ? "bg-white dark:bg-gray-800 border-indigo-400 dark:border-indigo-500 ring-2 ring-indigo-500/15 shadow-sm"
                            : "bg-white dark:bg-gray-800 border-gray-100 dark:border-gray-700/80 hover:border-gray-200 dark:hover:border-gray-600 shadow-sm"
                    }`}
                >
                    <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                            Toplam İşlem
                        </span>
                        <div className="w-9 h-9 rounded-xl bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 flex items-center justify-center">
                            <Server className="w-4 h-4" />
                        </div>
                    </div>
                    <div className="text-2xl font-bold text-gray-900 dark:text-white mt-2">
                        {summary.total}
                    </div>
                    <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
                        Son işlem: {formatDateTime(summary.lastRunUtc)}
                    </p>
                </button>

                {/* Kuyrukta Bekleyen */}
                <button
                    type="button"
                    onClick={() => {
                        setStatusFilter("pending");
                        setPage(1);
                    }}
                    className={`text-left rounded-2xl p-4 border transition-all ${
                        statusFilter === "pending"
                            ? "bg-white dark:bg-gray-800 border-amber-400 dark:border-amber-500 ring-2 ring-amber-500/15 shadow-sm"
                            : "bg-white dark:bg-gray-800 border-gray-100 dark:border-gray-700/80 hover:border-gray-200 dark:hover:border-gray-600 shadow-sm"
                    }`}
                >
                    <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold uppercase tracking-wider text-amber-600 dark:text-amber-400">
                            Kuyrukta Bekleyen
                        </span>
                        <div className="w-9 h-9 rounded-xl bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 flex items-center justify-center">
                            <Clock className="w-4 h-4" />
                        </div>
                    </div>
                    <div className="text-2xl font-bold text-gray-900 dark:text-white mt-2">
                        {summary.pending}
                    </div>
                    <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
                        Cihaza iletilmek üzere sırada
                    </p>
                </button>

                {/* Cihaza Gönderiliyor */}
                <button
                    type="button"
                    onClick={() => {
                        setStatusFilter("running");
                        setPage(1);
                    }}
                    className={`text-left rounded-2xl p-4 border transition-all ${
                        statusFilter === "running"
                            ? "bg-white dark:bg-gray-800 border-blue-400 dark:border-blue-500 ring-2 ring-blue-500/15 shadow-sm"
                            : "bg-white dark:bg-gray-800 border-gray-100 dark:border-gray-700/80 hover:border-gray-200 dark:hover:border-gray-600 shadow-sm"
                    }`}
                >
                    <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold uppercase tracking-wider text-blue-600 dark:text-blue-400">
                            Gönderiliyor
                        </span>
                        <div className="w-9 h-9 rounded-xl bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex items-center justify-center">
                            <Activity className="w-4 h-4" />
                        </div>
                    </div>
                    <div className="text-2xl font-bold text-gray-900 dark:text-white mt-2">
                        {summary.running}
                    </div>
                    <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
                        Şu an cihaza yazılıyor
                    </p>
                </button>

                {/* Cihaza İletildi */}
                <button
                    type="button"
                    onClick={() => {
                        setStatusFilter("succeeded");
                        setPage(1);
                    }}
                    className={`text-left rounded-2xl p-4 border transition-all ${
                        statusFilter === "succeeded"
                            ? "bg-white dark:bg-gray-800 border-emerald-400 dark:border-emerald-500 ring-2 ring-emerald-500/15 shadow-sm"
                            : "bg-white dark:bg-gray-800 border-gray-100 dark:border-gray-700/80 hover:border-gray-200 dark:hover:border-gray-600 shadow-sm"
                    }`}
                >
                    <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold uppercase tracking-wider text-emerald-600 dark:text-emerald-400">
                            Cihaza İletildi
                        </span>
                        <div className="w-9 h-9 rounded-xl bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 flex items-center justify-center">
                            <ShieldCheck className="w-4 h-4" />
                        </div>
                    </div>
                    <div className="text-2xl font-bold text-gray-900 dark:text-white mt-2">
                        {summary.succeeded}
                    </div>
                    <p className="text-[11px] text-emerald-600 dark:text-emerald-400 mt-1">
                        Son 24 saatte +{summary.succeededLast24h} onaylandı
                    </p>
                </button>

                {/* Hata Alan / Gitmeyen */}
                <button
                    type="button"
                    onClick={() => {
                        setStatusFilter("failed");
                        setPage(1);
                    }}
                    className={`text-left rounded-2xl p-4 border transition-all ${
                        statusFilter === "failed"
                            ? "bg-white dark:bg-gray-800 border-rose-400 dark:border-rose-500 ring-2 ring-rose-500/15 shadow-sm"
                            : "bg-white dark:bg-gray-800 border-gray-100 dark:border-gray-700/80 hover:border-gray-200 dark:hover:border-gray-600 shadow-sm"
                    }`}
                >
                    <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold uppercase tracking-wider text-rose-600 dark:text-rose-400">
                            Hata Alan / Gitmeyen
                        </span>
                        <div className="w-9 h-9 rounded-xl bg-rose-50 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 flex items-center justify-center">
                            <ShieldAlert className="w-4 h-4" />
                        </div>
                    </div>
                    <div className="text-2xl font-bold text-gray-900 dark:text-white mt-2">
                        {summary.failed}
                    </div>
                    <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
                        İletilmeyen toplam: {summary.unprocessed} kayıt
                    </p>
                </button>
            </div>

            {/* Cihaz Bazlı Yetki İletim Durumu */}
            {summary.deviceStats && summary.deviceStats.length > 0 && (
                <div className="rounded-2xl bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700/80 shadow-sm p-5">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
                        <div>
                            <h2 className="text-sm font-bold text-gray-900 dark:text-white flex items-center gap-2">
                                <Cpu className="w-4 h-4 text-indigo-500" />
                                Cihaz Bazlı Yetki İletim Durumu
                            </h2>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                Hangi cihaza kaç yetkinin ulaştığını, beklediğini veya hata aldığını cihaz bazında inceleyin. Filtrelemek için cihaza tıklayın.
                            </p>
                        </div>
                        {selectedDeviceId && (
                            <button
                                type="button"
                                onClick={() => {
                                    setSelectedDeviceId("");
                                    setPage(1);
                                }}
                                className="inline-flex items-center gap-1.5 text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:underline"
                            >
                                <X className="w-3.5 h-3.5" />
                                Cihaz Filtresini Temizle
                            </button>
                        )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3.5">
                        {summary.deviceStats.map((dev) => {
                            const isSelected = String(selectedDeviceId) === String(dev.deviceId);
                            const totalDev = dev.total || 0;
                            const pctSuccess = totalDev > 0 ? Math.round((dev.succeeded / totalDev) * 100) : 0;

                            return (
                                <div
                                    key={dev.deviceId}
                                    onClick={() => {
                                        setSelectedDeviceId(isSelected ? "" : String(dev.deviceId));
                                        setPage(1);
                                    }}
                                    className={`cursor-pointer rounded-2xl p-4 border transition-all ${
                                        isSelected
                                            ? "bg-indigo-50/40 dark:bg-indigo-950/30 border-indigo-400 dark:border-indigo-500 ring-2 ring-indigo-500/15"
                                            : "bg-gray-50/60 dark:bg-gray-900/40 border-gray-200/70 dark:border-gray-700/70 hover:border-indigo-300 dark:hover:border-indigo-700"
                                    }`}
                                >
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="min-w-0">
                                            <div className="flex items-center gap-2">
                                                <span
                                                    className={`w-2 h-2 rounded-full shrink-0 ${
                                                        dev.failed > 0
                                                            ? "bg-rose-500"
                                                            : dev.pending > 0 || dev.running > 0
                                                            ? "bg-amber-500 animate-pulse"
                                                            : "bg-emerald-500"
                                                    }`}
                                                />
                                                <h3 className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                                                    {dev.deviceName}
                                                </h3>
                                            </div>
                                            <div className="flex flex-wrap items-center gap-2 mt-1 text-[11px] text-gray-500 dark:text-gray-400">
                                                {dev.brand && (
                                                    <span className="px-2 py-0.5 rounded-md bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 font-medium">
                                                        {dev.brand}
                                                    </span>
                                                )}
                                                {dev.ipAddress && <span>IP: {dev.ipAddress}</span>}
                                            </div>
                                        </div>

                                        {canRun && (
                                            <div
                                                className="flex items-center gap-1 shrink-0"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                {dev.failed > 0 && (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRequeueFailed(dev.deviceId)}
                                                        disabled={runningAction}
                                                        className="p-1.5 rounded-lg bg-amber-100/80 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 hover:bg-amber-200/80 transition-colors"
                                                        title="Bu cihazdaki hatalı işlemleri tekrarla"
                                                    >
                                                        <RotateCcw className="w-3.5 h-3.5" />
                                                    </button>
                                                )}
                                                <button
                                                    type="button"
                                                    onClick={() => handlePushActive(dev.deviceId)}
                                                    disabled={runningAction}
                                                    className="p-1.5 rounded-lg bg-indigo-100/80 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-200/80 transition-colors"
                                                    title="Bu cihaza aktif yetkileri yeniden gönder"
                                                >
                                                    <Send className="w-3.5 h-3.5" />
                                                </button>
                                            </div>
                                        )}
                                    </div>

                                    {/* Mini Counters */}
                                    <div className="grid grid-cols-4 gap-2 mt-3 pt-3 border-t border-gray-200/60 dark:border-gray-700/60 text-center">
                                        <div className="rounded-xl bg-white dark:bg-gray-800 p-1.5 border border-gray-100 dark:border-gray-700/60">
                                            <div className="text-[10px] text-gray-500 dark:text-gray-400">Bekleyen</div>
                                            <div className="text-xs font-bold text-amber-600 dark:text-amber-400">
                                                {dev.pending}
                                            </div>
                                        </div>
                                        <div className="rounded-xl bg-white dark:bg-gray-800 p-1.5 border border-gray-100 dark:border-gray-700/60">
                                            <div className="text-[10px] text-gray-500 dark:text-gray-400">Gönderilen</div>
                                            <div className="text-xs font-bold text-blue-600 dark:text-blue-400">
                                                {dev.running}
                                            </div>
                                        </div>
                                        <div className="rounded-xl bg-white dark:bg-gray-800 p-1.5 border border-gray-100 dark:border-gray-700/60">
                                            <div className="text-[10px] text-gray-500 dark:text-gray-400">İletilen</div>
                                            <div className="text-xs font-bold text-emerald-600 dark:text-emerald-400">
                                                {dev.succeeded}
                                            </div>
                                        </div>
                                        <div className="rounded-xl bg-white dark:bg-gray-800 p-1.5 border border-gray-100 dark:border-gray-700/60">
                                            <div className="text-[10px] text-gray-500 dark:text-gray-400">Hatalı</div>
                                            <div className="text-xs font-bold text-rose-600 dark:text-rose-400">
                                                {dev.failed}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Progress Bar */}
                                    <div className="mt-2.5 flex items-center gap-2">
                                        <div className="flex-1 h-1.5 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                                            <div
                                                className="h-full bg-emerald-500 transition-all"
                                                style={{ width: `${pctSuccess}%` }}
                                            />
                                        </div>
                                        <span className="text-[11px] font-medium text-gray-500 dark:text-gray-400">
                                            %{pctSuccess}
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Main Queue Card */}
            <div className="rounded-2xl bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700/80 shadow-sm overflow-hidden">
                {/* Filter Bar */}
                <div className="p-4 sm:p-5 border-b border-gray-100 dark:border-gray-700/70 bg-gray-50/50 dark:bg-gray-900/30 space-y-4">
                    {/* Top Row: Segmented Status Pills & Category Toggle */}
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                        <div className="inline-flex flex-wrap items-center gap-1 p-1 rounded-xl bg-gray-200/70 dark:bg-gray-900/70 border border-gray-200/60 dark:border-gray-700/60">
                            {[
                                { key: "all", label: `Tümü (${summary.total})` },
                                { key: "unprocessed", label: `Gitmeyen / Bekleyen (${summary.unprocessed})` },
                                { key: "pending", label: `Kuyrukta (${summary.pending})` },
                                { key: "running", label: `Gönderiliyor (${summary.running})` },
                                { key: "succeeded", label: `İletilen (${summary.succeeded})` },
                                { key: "failed", label: `Hatalı (${summary.failed})` },
                            ].map((tab) => (
                                <button
                                    key={tab.key}
                                    type="button"
                                    onClick={() => {
                                        setStatusFilter(tab.key);
                                        setPage(1);
                                    }}
                                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                                        statusFilter === tab.key
                                            ? "bg-white dark:bg-gray-800 text-indigo-600 dark:text-indigo-400 shadow-sm"
                                            : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
                                    }`}
                                >
                                    {tab.label}
                                </button>
                            ))}
                        </div>

                        {/* Category Switch: Sadece Yetki vs Tüm İşlemler */}
                        <div className="inline-flex items-center gap-1 p-1 rounded-xl bg-gray-200/70 dark:bg-gray-900/70 border border-gray-200/60 dark:border-gray-700/60 self-start">
                            <button
                                type="button"
                                onClick={() => {
                                    setCategoryFilter("access");
                                    setPage(1);
                                }}
                                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                                    categoryFilter === "access"
                                        ? "bg-white dark:bg-gray-800 text-indigo-600 dark:text-indigo-400 shadow-sm"
                                        : "text-gray-600 dark:text-gray-400"
                                }`}
                            >
                                Yetki & Kimlik İşlemleri
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    setCategoryFilter("all");
                                    setPage(1);
                                }}
                                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                                    categoryFilter === "all"
                                        ? "bg-white dark:bg-gray-800 text-indigo-600 dark:text-indigo-400 shadow-sm"
                                        : "text-gray-600 dark:text-gray-400"
                                }`}
                            >
                                Tüm Cihaz İşlemleri
                            </button>
                        </div>
                    </div>

                    {/* Second Row: Search, Device Select, Operation Type Select */}
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
                        {/* Search Input */}
                        <div className="md:col-span-5 relative">
                            <Search className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                            <input
                                type="text"
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                placeholder="Personel adı, kart no, cihaz adı, IP veya hata mesajı ara…"
                                className="w-full pl-10 pr-9 py-2.5 text-xs sm:text-sm rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 transition-all"
                            />
                            {searchText && (
                                <button
                                    type="button"
                                    onClick={() => setSearchText("")}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            )}
                        </div>

                        {/* Device Filter */}
                        <div className="md:col-span-4">
                            <select
                                value={selectedDeviceId}
                                onChange={(e) => {
                                    setSelectedDeviceId(e.target.value);
                                    setPage(1);
                                }}
                                className="w-full px-3.5 py-2.5 text-xs sm:text-sm rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 transition-all"
                            >
                                <option value="">Tüm Cihazlar</option>
                                {(summary.deviceStats || []).map((d) => (
                                    <option key={d.deviceId} value={d.deviceId}>
                                        {d.deviceName} {d.ipAddress ? `(${d.ipAddress})` : ""} — Bekleyen: {d.pending}, Hatalı: {d.failed}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Operation Type Filter */}
                        <div className="md:col-span-3">
                            <div className="relative">
                                <Filter className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                                <select
                                    value={commandTypeFilter}
                                    onChange={(e) => {
                                        setCommandTypeFilter(e.target.value);
                                        setPage(1);
                                    }}
                                    className="w-full pl-9 pr-3.5 py-2.5 text-xs sm:text-sm rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 transition-all"
                                >
                                    <option value="all">Tüm İşlem Türleri</option>
                                    <option value="CardUpsert">Kart / Kişi Yetkisi Tanımlama</option>
                                    <option value="CardDelete">Kart / Kişi Yetkisi İptali</option>
                                    <option value="SetUserRight">Kapı Geçiş Planı Yetkisi</option>
                                    <option value="FingerPrintUpsert">Parmak İzi Tanımlama</option>
                                    <option value="FingerPrintDelete">Parmak İzi Silme</option>
                                    {categoryFilter === "all" && (
                                        <>
                                            <option value="OpenDoor">Uzaktan Kapı Açma</option>
                                            <option value="RemoteGrant">Uzaktan Geçiş Onayı</option>
                                            <option value="SyncTime">Cihaz Saat Eşitleme</option>
                                            <option value="SetBluetoothCfg">Bluetooth Ayarı Gönderimi</option>
                                            <option value="SetPdksKey">Cihaz Güvenlik Anahtarı</option>
                                        </>
                                    )}
                                </select>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Queue Table */}
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="border-b border-gray-100 dark:border-gray-700/70 bg-gray-50/70 dark:bg-gray-900/40 text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                                <th className="py-3.5 px-4">Durum</th>
                                <th className="py-3.5 px-4">İşlem & Kimlik</th>
                                <th className="py-3.5 px-4">Personel & Yetki Grubu</th>
                                <th className="py-3.5 px-4">Hedef Cihaz</th>
                                <th className="py-3.5 px-4">Sonuç / Hata Detayı</th>
                                <th className="py-3.5 px-4">Gönderim / İşlem Zamanı</th>
                                <th className="py-3.5 px-4 text-right">Aksiyon</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-gray-700/60 text-sm">
                            {loading ? (
                                <tr>
                                    <td colSpan={7} className="py-16 text-center">
                                        <div className="inline-flex flex-col items-center gap-2 text-gray-500 dark:text-gray-400">
                                            <Loader2 className="w-7 h-7 animate-spin text-indigo-500" />
                                            <span className="text-xs font-medium">
                                                Yetki senkronizasyon kayıtları yükleniyor…
                                            </span>
                                        </div>
                                    </td>
                                </tr>
                            ) : items.length === 0 ? (
                                <tr>
                                    <td colSpan={7} className="py-16 text-center">
                                        <div className="max-w-sm mx-auto flex flex-col items-center gap-2 text-gray-400 dark:text-gray-500">
                                            <ShieldCheck className="w-10 h-10 stroke-[1.5] text-gray-300 dark:text-gray-600" />
                                            <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                                                Kuyrukta kayıt bulunamadı
                                            </p>
                                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                                Seçilen filtre kriterlerine uygun gönderilmiş, bekleyen veya hatalı yetki işlemi yok.
                                            </p>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                items.map((row) => {
                                    const statusKey = resolveStatusKey(row);
                                    const statusMeta = STATUS_META[statusKey] || STATUS_META.pending;
                                    const opBadge = getOperationBadge(row);
                                    const OpIcon = opBadge.Icon;
                                    const rowId = row.id || row.taskId;

                                    return (
                                        <tr
                                            key={rowId}
                                            className="hover:bg-gray-50/70 dark:hover:bg-gray-700/25 transition-colors"
                                        >
                                            {/* Durum */}
                                            <td className="py-3.5 px-4 align-top whitespace-nowrap">
                                                <span
                                                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${statusMeta.badgeClass}`}
                                                >
                                                    <span className={`w-1.5 h-1.5 rounded-full ${statusMeta.dotClass}`} />
                                                    {statusMeta.label}
                                                </span>
                                            </td>

                                            {/* İşlem & Kimlik */}
                                            <td className="py-3.5 px-4 align-top">
                                                <div className="flex items-start gap-2.5">
                                                    <div
                                                        className={`w-8 h-8 rounded-xl border flex items-center justify-center shrink-0 mt-0.5 ${opBadge.color}`}
                                                    >
                                                        <OpIcon className="w-4 h-4" />
                                                    </div>
                                                    <div>
                                                        <div className="font-semibold text-gray-900 dark:text-white text-xs sm:text-sm">
                                                            {row.operation || "-"}
                                                        </div>
                                                        {row.targetSummary && (
                                                            <div className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-md bg-gray-100 dark:bg-gray-700/70 text-[11px] font-medium text-gray-700 dark:text-gray-300">
                                                                {row.targetSummary}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>

                                            {/* Personel & Yetki Grubu */}
                                            <td className="py-3.5 px-4 align-top">
                                                {row.employeeName ? (
                                                    <div>
                                                        <div className="font-semibold text-gray-900 dark:text-white text-xs sm:text-sm">
                                                            {row.employeeName}
                                                        </div>
                                                        {row.sicilNo && (
                                                            <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                                                                Sicil No: {row.sicilNo}
                                                            </div>
                                                        )}
                                                        {row.groupName && (
                                                            <div className="mt-1">
                                                                <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 border border-indigo-100 dark:border-indigo-800/50">
                                                                    {row.groupName}
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <span className="text-xs text-gray-400 dark:text-gray-500">
                                                        Cihaz İşlemi
                                                    </span>
                                                )}
                                            </td>

                                            {/* Hedef Cihaz */}
                                            <td className="py-3.5 px-4 align-top">
                                                <div className="font-semibold text-gray-900 dark:text-white text-xs sm:text-sm">
                                                    {row.deviceName || "-"}
                                                </div>
                                                <div className="flex flex-wrap items-center gap-1.5 mt-0.5 text-[11px] text-gray-500 dark:text-gray-400">
                                                    {row.deviceBrand && (
                                                        <span className="font-medium text-gray-700 dark:text-gray-300">
                                                            {row.deviceBrand}
                                                        </span>
                                                    )}
                                                    {row.deviceIp && <span>• {row.deviceIp}</span>}
                                                </div>
                                            </td>

                                            {/* Sonuç / Hata Detayı */}
                                            <td className="py-3.5 px-4 align-top max-w-xs">
                                                {statusKey === "failed" && row.lastError ? (
                                                    <div className="rounded-xl bg-rose-50/80 dark:bg-rose-900/20 border border-rose-200/70 dark:border-rose-800/50 px-2.5 py-1.5 text-xs text-rose-700 dark:text-rose-300 break-words">
                                                        {row.lastError}
                                                    </div>
                                                ) : (
                                                    <div className="text-xs text-gray-600 dark:text-gray-300 break-words">
                                                        {row.detail || "-"}
                                                    </div>
                                                )}
                                            </td>

                                            {/* Tarih */}
                                            <td className="py-3.5 px-4 align-top whitespace-nowrap text-xs text-gray-500 dark:text-gray-400">
                                                <div>
                                                    <span className="text-[10px] uppercase text-gray-400 dark:text-gray-500 block">
                                                        Gönderim:
                                                    </span>
                                                    {formatDateTime(row.createdAt || row.createdDate)}
                                                </div>
                                                {row.completedAt && (
                                                    <div className="mt-1">
                                                        <span className="text-[10px] uppercase text-gray-400 dark:text-gray-500 block">
                                                            Sonuçlanma:
                                                        </span>
                                                        {formatDateTime(row.completedAt)}
                                                    </div>
                                                )}
                                            </td>

                                            {/* Aksiyon */}
                                            <td className="py-3.5 px-4 align-top text-right whitespace-nowrap">
                                                <div className="inline-flex items-center justify-end gap-1.5">
                                                    {canRun && (statusKey === "failed" || statusKey === "running") && (
                                                        <button
                                                            type="button"
                                                            onClick={() => handleRetryRow(rowId)}
                                                            disabled={runningAction}
                                                            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-xl text-xs font-medium bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 border border-indigo-200/70 dark:border-indigo-800/60 hover:bg-indigo-100 dark:hover:bg-indigo-900/50 transition-colors"
                                                            title="Yeniden gönder"
                                                        >
                                                            <RotateCcw className="w-3.5 h-3.5" />
                                                            Tekrarla
                                                        </button>
                                                    )}

                                                    {canRun && statusKey === "pending" && (
                                                        <button
                                                            type="button"
                                                            onClick={() => handleCancelRow(rowId)}
                                                            disabled={runningAction}
                                                            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-xl text-xs font-medium bg-rose-50 dark:bg-rose-900/25 text-rose-700 dark:text-rose-300 border border-rose-200/70 dark:border-rose-800/60 hover:bg-rose-100 dark:hover:bg-rose-900/40 transition-colors"
                                                            title="Bekleyen işlemi iptal et"
                                                        >
                                                            <X className="w-3.5 h-3.5" />
                                                            İptal
                                                        </button>
                                                    )}

                                                    <button
                                                        type="button"
                                                        onClick={() => openDetailModal(row)}
                                                        className="p-1.5 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                                        title="Yetki İşlemi Detayını Gör"
                                                    >
                                                        <Eye className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination Footer */}
                <div className="px-5 py-3.5 border-t border-gray-100 dark:border-gray-700/70 bg-gray-50/50 dark:bg-gray-900/30 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-gray-500 dark:text-gray-400">
                    <div>
                        Toplam <strong className="text-gray-800 dark:text-gray-200">{totalCount}</strong> kayıttan{" "}
                        <strong className="text-gray-800 dark:text-gray-200">
                            {totalCount === 0 ? 0 : (page - 1) * pageSize + 1} -{" "}
                            {Math.min(page * pageSize, totalCount)}
                        </strong>{" "}
                        arası gösteriliyor
                    </div>

                    <div className="flex items-center gap-2">
                        <select
                            value={pageSize}
                            onChange={(e) => {
                                setPageSize(Number(e.target.value));
                                setPage(1);
                            }}
                            className="px-2.5 py-1.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 text-xs"
                        >
                            <option value={25}>25 / sayfa</option>
                            <option value={50}>50 / sayfa</option>
                            <option value={100}>100 / sayfa</option>
                        </select>

                        <button
                            type="button"
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                            disabled={page <= 1}
                            className="p-1.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-700"
                        >
                            <ChevronLeft className="w-4 h-4" />
                        </button>
                        <span className="px-2 font-medium text-gray-700 dark:text-gray-300">
                            Sayfa {page} / {totalPages}
                        </span>
                        <button
                            type="button"
                            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                            disabled={page >= totalPages}
                            className="p-1.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-700"
                        >
                            <ChevronRight className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Yetki İşlemi Detay Modalı */}
            {detailModalOpen && detailData && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div
                        className="fixed inset-0 bg-gray-900/70 backdrop-blur-sm"
                        onClick={() => setDetailModalOpen(false)}
                    />
                    <div className="relative w-full max-w-xl bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-700 overflow-hidden z-10 flex flex-col max-h-[88vh]">
                        <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700/70 flex items-center justify-between bg-gray-50/70 dark:bg-gray-900/40">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-2xl bg-indigo-50 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400 flex items-center justify-center">
                                    <ShieldCheck className="w-5 h-5" />
                                </div>
                                <div>
                                    <h3 className="text-base font-bold text-gray-900 dark:text-white">
                                        Yetki Gönderim Detayı
                                    </h3>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                        Cihaza iletilen yetki işleminin durum ve kapsam bilgileri
                                    </p>
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={() => setDetailModalOpen(false)}
                                className="p-2 rounded-xl text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="p-6 space-y-4 overflow-y-auto text-xs sm:text-sm">
                            {detailLoading && (
                                <div className="flex items-center gap-2 text-indigo-600 dark:text-indigo-400 text-xs">
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Detay bilgileri yükleniyor…
                                </div>
                            )}

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 rounded-2xl bg-gray-50/80 dark:bg-gray-900/40 border border-gray-100 dark:border-gray-700/70 p-4">
                                <div>
                                    <span className="text-[11px] text-gray-400 block">İşlem Türü</span>
                                    <strong className="text-gray-900 dark:text-white">
                                        {detailData.operation || detailData.Operation || "-"}
                                    </strong>
                                </div>
                                <div>
                                    <span className="text-[11px] text-gray-400 block">İletim Durumu</span>
                                    <strong className="text-gray-900 dark:text-white">
                                        {(STATUS_META[resolveStatusKey(detailData)] || STATUS_META.pending).label}
                                    </strong>
                                </div>
                                <div>
                                    <span className="text-[11px] text-gray-400 block">Personel</span>
                                    <strong className="text-gray-900 dark:text-white">
                                        {detailData.employeeName || detailData.EmployeeName || "Cihaz Sistem İşlemi"}
                                    </strong>
                                </div>
                                <div>
                                    <span className="text-[11px] text-gray-400 block">Sicil No</span>
                                    <strong className="text-gray-900 dark:text-white">
                                        {detailData.sicilNo || detailData.SicilNo || "—"}
                                    </strong>
                                </div>
                                <div>
                                    <span className="text-[11px] text-gray-400 block">Hedef Cihaz</span>
                                    <strong className="text-gray-900 dark:text-white">
                                        {detailData.deviceName || detailData.DeviceName || "-"}{" "}
                                        {detailData.deviceIp || detailData.DeviceIp
                                            ? `(${detailData.deviceIp || detailData.DeviceIp})`
                                            : ""}
                                    </strong>
                                </div>
                                <div>
                                    <span className="text-[11px] text-gray-400 block">Kimlik / Kart Bilgisi</span>
                                    <strong className="text-gray-900 dark:text-white">
                                        {detailData.targetSummary || detailData.cardNo || "—"}
                                    </strong>
                                </div>
                                <div>
                                    <span className="text-[11px] text-gray-400 block">Gönderim Zamanı</span>
                                    <strong className="text-gray-900 dark:text-white">
                                        {formatDateTime(detailData.createdAt || detailData.CreatedAt)}
                                    </strong>
                                </div>
                                <div>
                                    <span className="text-[11px] text-gray-400 block">Son İşlem Zamanı</span>
                                    <strong className="text-gray-900 dark:text-white">
                                        {formatDateTime(detailData.updatedAt || detailData.UpdatedAt)}
                                    </strong>
                                </div>
                            </div>

                            {(detailData.lastError || detailData.LastError) && (
                                <div className="rounded-2xl bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800/60 p-4">
                                    <div className="text-xs font-bold text-rose-700 dark:text-rose-300 mb-1">
                                        Hata Açıklaması
                                    </div>
                                    <div className="text-xs text-rose-600 dark:text-rose-200 break-words">
                                        {detailData.lastError || detailData.LastError}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-700/70 bg-gray-50/70 dark:bg-gray-900/40 flex items-center justify-end gap-2">
                            {canRun && (
                                <button
                                    type="button"
                                    onClick={async () => {
                                        await handleRetryRow(detailData.id || detailData.Id);
                                        setDetailModalOpen(false);
                                    }}
                                    className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white"
                                >
                                    <RotateCcw className="w-3.5 h-3.5" />
                                    Yeniden Gönder
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={() => setDetailModalOpen(false)}
                                className="px-4 py-2 text-xs font-semibold rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200"
                            >
                                Kapat
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
